import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, Select, MenuItem, FormControl, InputLabel, Box,
  Alert, AlertTitle, Typography, Divider, CircularProgress,
  IconButton, Chip, useTheme, useMediaQuery
} from '@mui/material';
import {
  Close as CloseIcon,
  Delete as DeleteIcon,
  Warning as WarningIcon,
  Edit as EditIcon
} from '@mui/icons-material';

// Boolean型安全変換ユーティリティ
import { ensureBoolean, ensureBooleanForAPI } from '../../utils/booleanUtils';

const SupplyEditDialog = ({ open, supply, onClose, onChange, onUpdate, onDelete, clients, products }) => {
  const [amountError, setAmountError] = useState('');
  const [dateError, setDateError] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // ダイアログが開かれたときの初期化
  useEffect(() => {
    if (open && supply) {
      setAmountError('');
      setDateError('');
      setShowDeleteConfirm(false);
      setIsDeleting(false);
      setIsUpdating(false);
    }
  }, [open, supply?.id]);

  if (!supply) return null;

  const handleChange = (field, value) => {
    onChange({ ...supply, [field]: value });
  };

  // 🚀 修正版：安全な顧客名取得
  const getClientName = (clientId) => {
    // データ検証
    if (!Array.isArray(clients) || clients.length === 0) {
      return '顧客データ取得中...';
    }
    
    if (!clientId) {
      return '顧客情報なし';
    }
    
    // 通常の検索
    let client = clients.find(c => c.id === clientId);
    
    // 型変換して再検索（数値 vs 文字列の問題対応）
    if (!client) {
      client = clients.find(c => c.id === Number(clientId)) || 
               clients.find(c => c.id === String(clientId));
    }
    
    // 他の可能性のあるプロパティ名もチェック
    if (!client) {
      client = clients.find(c => c.clientId === clientId);
    }
    
    if (!client) {
      return `顧客ID: ${clientId}`;
    }
    
    return client.name || `顧客ID: ${clientId}`;
  };

  // 🚀 修正版：安全な製品名取得
  const getCurrentProductName = () => {
    // supply.productNameがあれば最優先で使用
    if (supply.productName) {
      return supply.productName;
    }
    
    // products配列から検索
    if (Array.isArray(products) && supply.productId) {
      const product = products.find(p => p.id === supply.productId);
      if (product?.name) {
        return product.name;
      }
    }
    
    // 🔹 supply.clientNameがある場合（SupplyDTOから）
    if (supply.clientName) {
      // 顧客名が分かっている場合のデフォルト製品表示
      return '製品情報なし';
    }
    
    // 最後の手段：IDを表示
    return supply.productId ? `製品ID: ${supply.productId}` : '製品情報なし';
  };

  // 🚀 改善された更新処理
  const handleUpdateClick = async () => {
    setIsUpdating(true);
    try {
      await onUpdate();
    } catch (error) {

    } finally {
      setIsUpdating(false);
    }
  };

  // 🚀 改善された削除処理
  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    try {
      await onDelete();
      setShowDeleteConfirm(false);
    } catch (error) {
    } finally {
      setIsDeleting(false);
    }
  };

  // 🔹 日付フォーマット関数
  const formatDate = (dateString) => {
    if (!dateString) return '不明';
    try {
      return new Date(dateString).toLocaleDateString('ja-JP');
    } catch {
      return '不明';
    }
  };

  // 🚀 修正：Boolean値の安全な判定
  const isExceptionalValue = ensureBoolean(supply.isExceptional);

  const isInvalid = !!amountError || !!dateError || !supply.amount || !supply.supplyDate;

  return (
    <>
      {/* 🔹 メイン編集ダイアログ */}
      <Dialog 
        open={open && !showDeleteConfirm} 
        onClose={onClose} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: { 
            borderRadius: 2,
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          pb: 1,
          flexShrink: 0
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EditIcon color="primary" />
            <Typography variant="h6">
              供給履歴の編集
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        {/* スクロール可能なコンテンツエリア */}
        <DialogContent 
          dividers
          sx={{
            flex: 1,
            overflow: 'auto',
            WebkitOverflowScrolling: 'touch'
          }}
        >
          {/* 変更制限に関する案内 */}
          <Alert severity="info" sx={{ mb: 2 }}>
            <AlertTitle>編集可能な項目</AlertTitle>
            顧客と製品は変更できません。供給量、供給日、供給タイプのみ編集可能です。
          </Alert>

          {/* 🚀 修正版：顧客名表示（変更不可） */}
          <TextField
            label="顧客名"
            value={supply.clientName || getClientName(supply.clientId)}
            fullWidth
            InputProps={{ 
              readOnly: true,
              style: { backgroundColor: '#f8f9fa' }
            }}
            sx={{ mb: 2 }}
          />

          {/* 🚀 修正版：製品名表示（変更不可） */}
          <TextField
            label="製品"
            value={getCurrentProductName()}
            fullWidth
            InputProps={{ 
              readOnly: true,
              style: { backgroundColor: '#f8f9fa' }
            }}
            sx={{ mb: 2 }}
          />

          {/* 供給量（編集可能） */}
          <TextField
            label="供給量 (L)"
            fullWidth
            value={supply.amount}
            onChange={(e) => {
              const val = e.target.value;
              if (/^\d*$/.test(val)) {
                handleChange('amount', val);
                const num = parseInt(val, 10);
                if (val === "" || num < 1 || num > 200000) {
                  setAmountError("1〜200000の範囲で入力してください");
                } else {
                  setAmountError("");
                }
              }
            }}
            error={!!amountError}
            helperText={amountError}
            inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
            sx={{ mb: 2 }}
          />

          {/* 供給日（編集可能） */}
          <TextField
            label="供給日"
            type="date"
            fullWidth
            value={supply.supplyDate ? supply.supplyDate.substring(0, 10) : ""}
            onChange={(e) => {
              const newDate = e.target.value;
              const today = new Date().toISOString().split("T")[0];
              if (newDate > today) {
                setDateError("未来の日付は選択できません");
              } else {
                setDateError("");
              }
              handleChange('supplyDate', newDate);
            }}
            InputLabelProps={{ shrink: true }}
            inputProps={{ max: new Date().toISOString().split("T")[0] }}
            error={!!dateError}
            helperText={dateError}
            sx={{ mb: 2 }}
          />

          {/* 🚀 修正：供給タイプ（編集可能） - Boolean値の安全な変換 */}
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="supply-type-label">供給タイプ</InputLabel>
            <Select
              labelId="supply-type-label"
              value={isExceptionalValue}
              onChange={(e) => handleChange('isExceptional', ensureBooleanForAPI(e.target.value))}
              label="供給タイプ"
            >
              <MenuItem value={false}>通常</MenuItem>
              <MenuItem value={true}>特例</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>

        {/* 🚀 ハイブリッドレイアウト：デスクトップ横並び、モバイル縦並び */}
        <DialogActions sx={{ 
          p: isMobile ? 3 : 2,
          flexShrink: 0,
          // 🔧 デスクトップとモバイルでレイアウトを分岐
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? 1.5 : 1,
          alignItems: isMobile ? 'stretch' : 'center'
        }}>
          {isMobile ? (
            // モバイル用：縦並びレイアウト（現在のスタイル維持）
            <>
              <Button
                variant="contained"
                onClick={handleUpdateClick} 
                disabled={isInvalid || isUpdating}
                startIcon={isUpdating ? <CircularProgress size={16} /> : <EditIcon />}
                size="large"
                fullWidth
                sx={{ order: 1 }}
              >
                {isUpdating ? '更新中...' : '更新'}
              </Button>
              
              <Button 
                variant="text"
                onClick={onClose}
                disabled={isUpdating}
                color="inherit"
                size="medium"
                sx={{ order: 2, minWidth: 100 }}
              >
                キャンセル
              </Button>
              
              <Button
                onClick={() => setShowDeleteConfirm(true)}
                color="error"
                variant="outlined"
                startIcon={<DeleteIcon />}
                disabled={isUpdating}
                size="medium"
                sx={{ order: 3 }}
              >
                削除
              </Button>
            </>
          ) : (
            // デスクトップ用：横並びレイアウト（元のスタイル復活）
            <>
              <Button
                onClick={() => setShowDeleteConfirm(true)}
                color="error"
                variant="outlined"
                startIcon={<DeleteIcon />}
                sx={{ mr: 'auto' }}
                disabled={isUpdating}
              >
                削除
              </Button>

              <Button 
                onClick={onClose}
                disabled={isUpdating}
              >
                キャンセル
              </Button>
              
              <Button 
                onClick={handleUpdateClick} 
                variant="contained" 
                disabled={isInvalid || isUpdating}
                startIcon={isUpdating ? <CircularProgress size={16} /> : <EditIcon />}
              >
                {isUpdating ? '更新中...' : '更新'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* 🚀 削除確認ダイアログ - 同様にハイブリッドレイアウト */}
      <Dialog
        open={showDeleteConfirm}
        onClose={() => !isDeleting && setShowDeleteConfirm(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            border: '2px solid',
            borderColor: 'error.main',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1,
          color: 'error.main',
          flexShrink: 0
        }}>
          <WarningIcon />
          <Typography variant="h6">
            削除の確認
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ 
          flex: 1,
          overflow: 'auto',
          WebkitOverflowScrolling: 'touch'
        }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            <AlertTitle>注意</AlertTitle>
            この操作は元に戻せません。削除後はデータを復元できません。
          </Alert>
          
          <Typography variant="body1" gutterBottom>
            以下の供給履歴を削除しますか？
          </Typography>
          
          {/* 削除対象の詳細情報 */}
          <Box sx={{ 
            mt: 2, 
            p: 2, 
            bgcolor: 'grey.50', 
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'grey.300'
          }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              削除対象の詳細
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography variant="body2">
                <strong>顧客:</strong> {supply.clientName || getClientName(supply.clientId)}
              </Typography>
              <Typography variant="body2">
                <strong>製品:</strong> {getCurrentProductName()}
              </Typography>
              <Typography variant="body2">
                <strong>供給量:</strong> {supply.amount?.toLocaleString() || 0}L
              </Typography>
              <Typography variant="body2">
                <strong>供給日:</strong> {formatDate(supply.supplyDate)}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                <strong>タイプ:</strong>
                <Chip
                  label={isExceptionalValue ? '特例' : '通常'}
                  color={isExceptionalValue ? 'warning' : 'success'}
                  size="small"
                />
              </Box>
            </Box>
          </Box>
        </DialogContent>

        {/* 🚀 削除確認も同様にハイブリッドレイアウト */}
        <DialogActions sx={{ 
          p: isMobile ? 3 : 2,
          flexShrink: 0,
          // 🔧 デスクトップとモバイルでレイアウトを分岐
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? 1.5 : 1,
          alignItems: isMobile ? 'stretch' : 'center'
        }}>
          {isMobile ? (
            // モバイル用：縦並びレイアウト
            <>
              <Button
                onClick={handleDeleteConfirm}
                color="error"
                variant="contained"
                disabled={isDeleting}
                startIcon={isDeleting ? <CircularProgress size={16} /> : <DeleteIcon />}
                size="large"
                fullWidth
                sx={{ order: 1 }}
              >
                {isDeleting ? '削除中...' : '削除する'}
              </Button>
              
              <Button 
                variant="text"
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isDeleting}
                color="inherit"
                size="medium"
                sx={{ order: 2, minWidth: 100 }}
              >
                キャンセル
              </Button>
            </>
          ) : (
            // デスクトップ用：横並びレイアウト（元のスタイル復活）
            <>
              <Button 
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isDeleting}
              >
                キャンセル
              </Button>
              
              <Button
                onClick={handleDeleteConfirm}
                color="error"
                variant="contained"
                disabled={isDeleting}
                startIcon={isDeleting ? <CircularProgress size={16} /> : <DeleteIcon />}
              >
                {isDeleting ? '削除中...' : '削除する'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SupplyEditDialog;